import type { RouteRecord } from 'vite-react-ssg'
import { RootLayout } from './components/layout/RootLayout'

const loadHome = () => import('./pages/Home').then((m) => ({ Component: m.Home }))
const loadAbout = () => import('./pages/About').then((m) => ({ Component: m.About }))
const loadAmine = () =>
  import('./pages/MohamedAmineSaada').then((m) => ({
    Component: m.MohamedAmineSaada,
  }))
const loadServices = () => import('./pages/Services').then((m) => ({ Component: m.Services }))
const loadChatbotMorocco = () =>
  import('./pages/AiServiceLanding').then((m) => ({
    Component: m.ChatbotMorocco,
  }))
const loadAutomationMorocco = () =>
  import('./pages/AiServiceLanding').then((m) => ({
    Component: m.AutomationMorocco,
  }))
const loadN8nMorocco = () =>
  import('./pages/AiServiceLanding').then((m) => ({
    Component: m.N8nMorocco,
  }))
const loadMedicalMarketingAgency = () =>
  import('./pages/AcquisitionLanding').then((m) => ({
    Component: m.MedicalMarketingAgency,
  }))
const loadSlimmingCenterMarketing = () =>
  import('./pages/AcquisitionLanding').then((m) => ({
    Component: m.SlimmingCenterMarketing,
  }))
const loadAestheticDoctorMarketing = () =>
  import('./pages/AcquisitionLanding').then((m) => ({
    Component: m.AestheticDoctorMarketing,
  }))
const loadAestheticClinicMarketing = () =>
  import('./pages/AcquisitionLanding').then((m) => ({
    Component: m.AestheticClinicMarketing,
  }))
const loadContact = () => import('./pages/Contact').then((m) => ({ Component: m.Contact }))
const loadSchedule = () =>
  import('./pages/ScheduleRedirect').then((m) => ({
    Component: m.ScheduleRedirect,
  }))
const loadForm = () => import('./pages/Form').then((m) => ({ Component: m.Form }))
const loadThankYou = () => import('./pages/ThankYou').then((m) => ({ Component: m.ThankYou }))
const loadBooking = () => import('./pages/Booking').then((m) => ({ Component: m.Booking }))
const loadDoctorOnboarding = () => import('./pages/DoctorOnboarding').then((m) => ({ Component: m.DoctorOnboarding }))
const loadAutomations = () => import('./pages/Automations').then((m) => ({ Component: m.Automations }))
const loadDoctorsDmGuide = () =>
  import('./pages/DoctorsDmGuide').then((m) => ({
    Component: m.DoctorsDmGuide,
  }))
const loadDoctorsDmGuideThankYou = () =>
  import('./pages/DoctorsDmGuideThankYou').then((m) => ({
    Component: m.DoctorsDmGuideThankYou,
  }))
const loadDoctorsDmGuidePatients = () =>
  import('./pages/DoctorsDmGuideThankYou').then((m) => ({
    Component: m.DoctorsDmGuidePatients,
  }))
const loadResources = () => import('./pages/Resources').then((m) => ({ Component: m.Resources }))
const loadPrivacy = () => import('./pages/Privacy').then((m) => ({ Component: m.Privacy }))
const loadTerms = () => import('./pages/Terms').then((m) => ({ Component: m.Terms }))
const loadDentaire = () => import('./pages/Dentaire').then((m) => ({ Component: m.Dentaire }))
const loadCase = () =>
  import('./pages/CasablancaCaseStudy').then((m) => ({
    Component: m.CasablancaCaseStudy,
  }))
const loadDemo = () => import('./pages/DarijaDemo').then((m) => ({ Component: m.DarijaDemo }))
const loadProjectDemo = () => import('./pages/ProjectDemo').then((m) => ({ Component: m.ProjectDemo }))
// Route-level lazy, per AGENTS.md. React.lazy would prerender an empty fallback.
const loadDoctorsAgent = () => import('./pages/DoctorsAgent').then((m) => ({ Component: m.DoctorsAgent }))

const marketingPages: RouteRecord[] = [
  { path: 'about', lazy: loadAbout },
  { path: 'about/mohamed-amine-saada', lazy: loadAmine },
  { path: 'services', lazy: loadServices },
  { path: 'contact', lazy: loadContact },
  { path: 'schedule', lazy: loadSchedule },
  { path: 'form', lazy: loadForm },
  { path: 'thank-you', lazy: loadThankYou },
  { path: 'book-demo', lazy: loadBooking },
  { path: 'automations', lazy: loadAutomations },
  { path: 'resources', lazy: loadResources },
  { path: 'privacy', lazy: loadPrivacy },
  { path: 'terms', lazy: loadTerms },
]

const frenchCanonicalPages: RouteRecord[] = [
  { path: 'dentaire', lazy: loadDentaire },
  {
    path: 'case-studies/clinique-dentaire-casablanca-agent-ia-whatsapp',
    lazy: loadCase,
  },
  { path: 'projects/demo-agent-ia-whatsapp-darija', lazy: loadDemo },
  { path: 'projects/enrichissement-leads-ia', lazy: loadProjectDemo },
  {
    path: 'projects/landing-page-dentaire-automatisation-whatsapp',
    lazy: loadProjectDemo,
  },
  { path: 'projects/site-cabinet-avocats', lazy: loadProjectDemo },
  { path: 'projects/plateforme-traduction-commandes', lazy: loadProjectDemo },
]

function prefixRoutes(prefix: string, routes: RouteRecord[]): RouteRecord[] {
  return routes.map((route) => ({ ...route, path: `${prefix}/${route.path}` }))
}

export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, lazy: loadHome },
      { path: 'fr/configurer-assistant', lazy: loadDoctorOnboarding },
      ...marketingPages,
      {
        // Bare URL: canonical for the 19 English-original articles (English
        // is DEFAULT_LOCALE) and also for the 12 French-original articles
        // (French is their canonical locale, see FRENCH_CANONICAL in
        // src/i18n/locale.ts) — resourceArticlePath() already knows which is
        // which, so every route below just filters RESOURCES by matching its
        // own prefix against that single source of truth.
        path: 'resources/:slug',
        lazy: () =>
          import('./pages/ResourceArticle').then((m) => ({
            Component: m.ResourceArticle,
          })),
        getStaticPaths: async () => {
          const { RESOURCES, resourceArticlePath } = await import('./data/resources')
          return RESOURCES.filter((resource) => resourceArticlePath(resource) === `/resources/${resource.slug}`).map(
            (resource) => `resources/${resource.slug}`,
          )
        },
      },
      {
        // English translations of the 12 French-original articles.
        path: 'en/resources/:slug',
        lazy: () =>
          import('./pages/ResourceArticle').then((m) => ({
            Component: m.ResourceArticle,
          })),
        getStaticPaths: async () => {
          const { RESOURCES, resourceArticlePath } = await import('./data/resources')
          return RESOURCES.filter((resource) => resourceArticlePath(resource) === `/en/resources/${resource.slug}`).map(
            (resource) => `en/resources/${resource.slug}`,
          )
        },
      },
      {
        // French translations of 19 of the English-original resource
        // articles (see resourceTranslations.fr.ts).
        path: 'fr/resources/:slug',
        lazy: () =>
          import('./pages/ResourceArticle').then((m) => ({
            Component: m.ResourceArticle,
          })),
        getStaticPaths: async () => {
          const { RESOURCES, resourceArticlePath } = await import('./data/resources')
          return RESOURCES.filter((resource) => resourceArticlePath(resource) === `/fr/resources/${resource.slug}`).map(
            (resource) => `fr/resources/${resource.slug}`,
          )
        },
      },
      {
        // Arabic translations of the 19 English-original articles (see
        // resourceTranslations.ar.ts) plus the 12 French-original articles
        // (see resourceFirstPatients.ar.ts / resourceAdditions.ar.ts).
        path: 'ar/resources/:slug',
        lazy: () =>
          import('./pages/ResourceArticle').then((m) => ({
            Component: m.ResourceArticle,
          })),
        getStaticPaths: async () => {
          const { RESOURCES, resourceArticlePath } = await import('./data/resources')
          return RESOURCES.filter((resource) => resourceArticlePath(resource) === `/ar/resources/${resource.slug}`).map(
            (resource) => `ar/resources/${resource.slug}`,
          )
        },
      },
      ...frenchCanonicalPages,
      {
        path: 'discover',
        lazy: () => import('./pages/Discover').then((m) => ({ Component: m.Discover })),
      },
      {
        path: 'offer',
        lazy: () => import('./pages/Offer').then((m) => ({ Component: m.Offer })),
      },
      {
        path: 'morocco-doctors',
        lazy: () =>
          import('./pages/MoroccoDoctors').then((m) => ({
            Component: m.MoroccoDoctors,
          })),
      },
      {
        path: 'fr/services/:industrySlug',
        lazy: () => import('./pages/AppointmentIndustry').then((m) => ({ Component: m.AppointmentIndustry })),
        getStaticPaths: async () => {
          const { APPOINTMENT_INDUSTRY_PATHS } = await import('./data/appointmentIndustries')
          return APPOINTMENT_INDUSTRY_PATHS.map((path) => path.replace(/^\//, ''))
        },
      },
      { path: 'doctors-agent', lazy: loadDoctorsAgent },
      { path: 'doctors-agent-2', lazy: loadDoctorsAgent },
      {
        path: 'doctors-agent/rdv',
        lazy: () =>
          import('./pages/DoctorsAgentBooking').then((m) => ({
            Component: m.DoctorsAgentBooking,
          })),
      },
      {
        path: 'doctors-agent/merci',
        lazy: () =>
          import('./pages/DoctorsAgentThankYou').then((m) => ({
            Component: m.DoctorsAgentThankYou,
          })),
      },
      {
        path: 'doctors-agent/merci-non-qualifie',
        lazy: () =>
          import('./pages/DoctorsAgentUnqualifiedThankYou').then((m) => ({
            Component: m.DoctorsAgentUnqualifiedThankYou,
          })),
      },
      {
        path: 'doctors-agent-2/merci',
        lazy: () =>
          import('./pages/DoctorsAgent2ThankYou').then((m) => ({
            Component: m.DoctorsAgent2ThankYou,
          })),
      },
      {
        path: 'doctors-agent-2/merci-non-qualifie',
        lazy: () =>
          import('./pages/DoctorsAgentUnqualifiedThankYou').then((m) => ({
            Component: m.DoctorsAgentUnqualifiedThankYou,
          })),
      },
      { path: 'automations/convertir-vos-dm-en-rdv', lazy: loadDoctorsDmGuide },
      {
        path: 'automations/convertir-vos-dm-en-rdv/merci',
        lazy: loadDoctorsDmGuideThankYou,
      },
      {
        path: 'automations/convertir-vos-dm-en-rdv/merci-patients',
        lazy: loadDoctorsDmGuidePatients,
      },
      {
        path: 'ma/:locale',
        lazy: () =>
          import('./pages/MoroccoHub').then((m) => ({
            Component: m.MoroccoHub,
          })),
        getStaticPaths: () => ['ma/fr', 'ma/ar', 'ma/en'],
      },
      {
        path: 'ma/:locale/specialites/:specialtySlug',
        lazy: () =>
          import('./pages/MedicalSpecialty').then((m) => ({
            Component: m.MedicalSpecialty,
          })),
        getStaticPaths: async () => {
          const { MEDICAL_SPECIALTY_PATHS } = await import('./data/medicalSpecialties')
          return MEDICAL_SPECIALTY_PATHS
        },
      },
      {
        path: 'ma/:locale/guides',
        lazy: () =>
          import('./pages/LocalSeoGuidesHub').then((m) => ({
            Component: m.LocalSeoGuidesHub,
          })),
        getStaticPaths: () => ['ma/fr/guides', 'ma/en/guides', 'ma/ar/guides'],
      },
      {
        // Programmatic city and specialty guides. One component, one route,
        // paths generated from the approved records in src/data/local-seo.
        // French and English are both generated; Arabic is not (see locale.ts).
        path: 'ma/:locale/guides/:pageSlug',
        lazy: () =>
          import('./pages/LocalSeoGuide').then((m) => ({
            Component: m.LocalSeoGuide,
          })),
        getStaticPaths: async () => {
          const { LOCAL_SEO_PATHS } = await import('./data/local-seo/qualityGate')
          return LOCAL_SEO_PATHS
        },
      },
      {
        path: 'ma/:locale/villes/:citySlug/agent-ia-cabinet-dentaire',
        lazy: () =>
          import('./pages/MoroccoDentalCity').then((m) => ({
            Component: m.MoroccoDentalCity,
          })),
        getStaticPaths: async () => {
          const { MOROCCO_CITY_PATHS } = await import('./data/moroccoLocations')
          return MOROCCO_CITY_PATHS
        },
      },
      { path: 'fr', lazy: loadHome },
      { path: 'ar', lazy: loadHome },
      { path: 'fr/services/chatbot-ia-maroc', lazy: loadChatbotMorocco },
      {
        path: 'fr/services/automatisation-ia-maroc',
        lazy: loadAutomationMorocco,
      },
      { path: 'fr/services/n8n-maroc', lazy: loadN8nMorocco },
      { path: 'fr/services/agence-marketing-digital-maroc', lazy: loadMedicalMarketingAgency },
      { path: 'fr/services/marketing-centre-amincissement', lazy: loadSlimmingCenterMarketing },
      { path: 'fr/services/marketing-medecin-esthetique', lazy: loadAestheticDoctorMarketing },
      { path: 'fr/services/marketing-clinique-esthetique', lazy: loadAestheticClinicMarketing },
      ...prefixRoutes('fr', marketingPages),
      ...prefixRoutes('ar', marketingPages),
      ...prefixRoutes('en', frenchCanonicalPages),
      ...prefixRoutes('ar', frenchCanonicalPages),
      {
        path: '*',
        lazy: () => import('./pages/NotFound').then((m) => ({ Component: m.NotFound })),
      },
    ],
  },
]
